import { type CSSProperties, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { siteConfig } from '../config/site';
import { useMoonbase } from '../lib/moonbase';
import { CookieConsent } from './CookieConsent';
import { Icon } from './Icon';
import { MoonbaseSetupDialog, MoonbaseSetupNotice } from './MoonbaseSetup';
import { NewsletterForm } from './NewsletterForm';

function BrandMark() {
  return (
    <Link className="brand" to="/" aria-label={`${siteConfig.brand.name} home`}>
      <span className="brand__mark" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      <span className="brand__wordmark">
        <strong>{siteConfig.brand.shortName}</strong>
        <small>Audio</small>
      </span>
    </Link>
  );
}

function StoreControls({ mobile = false }: { mobile?: boolean }) {
  const { configured, signIn, viewAccount, viewCart, openSetup, status } = useMoonbase();
  const className = mobile ? 'mobile-store-controls' : 'header__store';

  if (!configured) {
    return (
      <div className={className}>
        <button className="text-button" type="button" onClick={openSetup}>
          <Icon name="account" />
          <span>Account</span>
        </button>
        <button className="icon-button" type="button" onClick={openSetup} aria-label="Open cart setup">
          <Icon name="bag" />
        </button>
      </div>
    );
  }

  return (
    <div className={className} data-store-status={status}>
      <button
        className="text-button"
        type="button"
        hidden
        data-moonbase-if="!user"
        onClick={signIn}
      >
        <Icon name="account" />
        <span>Sign in</span>
      </button>
      <button
        className="text-button"
        type="button"
        hidden
        data-moonbase-if="user"
        onClick={viewAccount}
      >
        <Icon name="account" />
        <span data-moonbase-render="user.name">My account</span>
      </button>
      <button className="icon-button cart-button" type="button" onClick={viewCart} aria-label="Open shopping cart">
        <Icon name="bag" />
        <span className="cart-count" hidden data-moonbase-if="cart.has_items" data-moonbase-render="cart.item_count">0</span>
      </button>
    </div>
  );
}

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('menu-open', mobileOpen);
    return () => document.body.classList.remove('menu-open');
  }, [mobileOpen]);

  const navItems = [
    { to: '/plugins', label: 'Plugins' },
    { to: '/about', label: 'About' },
    { to: '/support', label: 'Support' },
    { to: '/changelog', label: 'Updates' },
  ];

  return (
    <>
      {siteConfig.announcement.enabled && (
        <Link className="announcement" to={siteConfig.announcement.href}>
          <span>{siteConfig.announcement.text}</span>
          <span>{siteConfig.announcement.linkLabel}<Icon name="arrow" /></span>
        </Link>
      )}
      <header className="site-header">
        <div className="shell header__inner">
          <BrandMark />
          <nav className="desktop-nav" aria-label="Primary navigation">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? 'active' : undefined)}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <StoreControls />
          <button
            className="mobile-menu-button"
            type="button"
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen((value) => !value)}
          >
            <Icon name={mobileOpen ? 'close' : 'menu'} />
            <span className="sr-only">{mobileOpen ? 'Close menu' : 'Open menu'}</span>
          </button>
        </div>
      </header>
      <div id="mobile-navigation" className={`mobile-nav${mobileOpen ? ' mobile-nav--open' : ''}`} aria-hidden={!mobileOpen}>
        <nav aria-label="Mobile navigation">
          {navItems.map((item, index) => (
            <NavLink key={item.to} to={item.to} style={{ '--nav-index': index } as CSSProperties}>
              <span>0{index + 1}</span>{item.label}<Icon name="arrow" />
            </NavLink>
          ))}
        </nav>
        <StoreControls mobile />
        <p>{siteConfig.brand.tagline}</p>
      </div>
    </>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer">
      <div className="shell footer__top">
        <div className="footer__brand">
          <BrandMark />
          <p>{siteConfig.brand.description}</p>
          <div className="footer__formats" aria-label="Supported formats">
            {siteConfig.compatibility.formats.map((format) => <span key={format}>{format}</span>)}
          </div>
        </div>
        <div className="footer__links">
          <div>
            <strong>Products</strong>
            {siteConfig.products.map((product) => <Link key={product.slug} to={`/plugins/${product.slug}`}>{product.name}</Link>)}
            <Link to="/plugins">All plugins</Link>
          </div>
          <div>
            <strong>Company</strong>
            <Link to="/about">About</Link>
            <Link to="/changelog">Updates</Link>
            <a href={`mailto:${siteConfig.company.pressEmail}`}>Press</a>
          </div>
          <div>
            <strong>Help</strong>
            <Link to="/support">Support center</Link>
            <Link to="/account">Downloads & licenses</Link>
            <a href={`mailto:${siteConfig.company.supportEmail}`}>Email support</a>
          </div>
          <div>
            <strong>Legal</strong>
            <Link to="/legal/privacy">Privacy</Link>
            <Link to="/legal/terms">Terms</Link>
            <Link to="/legal/eula">EULA</Link>
            <Link to="/legal/refunds">Refunds</Link>
            <Link to="/legal/security">Security</Link>
          </div>
        </div>
      </div>
      <div className="shell footer__newsletter">
        <span>Occasional release notes</span>
        <NewsletterForm compact />
      </div>
      <div className="shell footer__bottom">
        <span>© {year} {siteConfig.company.legalName}</span>
        <span>Designed for careful listening.</span>
        <div className="footer__social">
          {siteConfig.social.instagram && <a href={siteConfig.social.instagram} rel="noreferrer" target="_blank">Instagram</a>}
          {siteConfig.social.youtube && <a href={siteConfig.social.youtube} rel="noreferrer" target="_blank">YouTube</a>}
          {siteConfig.social.soundcloud && <a href={siteConfig.social.soundcloud} rel="noreferrer" target="_blank">SoundCloud</a>}
        </div>
      </div>
    </footer>
  );
}

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);
  return null;
}

export function Layout() {
  return (
    <div className="site-frame">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <ScrollToTop />
      <Header />
      <main id="main-content"><Outlet /></main>
      <Footer />
      <MoonbaseSetupDialog />
      <MoonbaseSetupNotice />
      <CookieConsent />
    </div>
  );
}
